/**
 * Sub-Items List Component for Timeline V2
 *
 * Renders indented list of sub-items with icons and connecting lines.
 *
 * Used for:
 * - Individual property changes in bundled ahoy_property_updated events
 * - Multi-step actions
 *
 * Visual structure (from Figma):
 * - Indented 32px (ml-8)
 * - Small vertical connecting line on left
 * - Icons (18px) + text for each item
 * - Links for URL-type properties
 */

import { useCallback, useState } from 'react';

import { TIMELINE_DESIGN_TOKENS } from '../../design-tokens';
import type { SubItemContent } from '../../types/renderer';

export interface SubItemsListProps {
  items: SubItemContent[];
  className?: string;
  /**
   * When set and `items.length` exceeds it, only the first `collapseAfter`
   * items render with a "+N more changes" toggle revealing the rest. Keeps
   * bulk multi-field updates from flooding the timeline. Omit (default) to
   * always render every item — preserves existing behaviour for all other
   * event types.
   */
  collapseAfter?: number;
  /**
   * Label for the collapsed remainder. Defaults to "+N more change(s)" —
   * pass one when the items are not changes (log lines, answers, …).
   */
  moreLabel?: (hiddenCount: number) => string;
}

function defaultMoreLabel(hiddenCount: number): string {
  return `+${hiddenCount} more ${hiddenCount === 1 ? 'change' : 'changes'}`;
}

/**
 * Container for sub-items list
 */
export function SubItemsList({
  items,
  className = '',
  collapseAfter,
  moreLabel = defaultMoreLabel,
}: SubItemsListProps) {
  const [expanded, setExpanded] = useState(false);
  const toggle = useCallback(() => setExpanded(prev => !prev), []);

  if (items.length === 0) {
    return null;
  }

  const shouldCollapse =
    collapseAfter != null && items.length > collapseAfter && !expanded;
  const visibleItems = shouldCollapse ? items.slice(0, collapseAfter) : items;
  const hiddenCount = items.length - visibleItems.length;
  const canToggle = collapseAfter != null && items.length > collapseAfter;

  return (
    <div
      className={`${TIMELINE_DESIGN_TOKENS.spacing.subItemIndent} ${TIMELINE_DESIGN_TOKENS.layout.subItemsContainer} ${className}`}
      role='list'
    >
      {visibleItems.map((item, index) => (
        // eslint-disable-next-line react/no-array-index-key -- Sub-items are parsed content fragments with no stable identity
        <div key={index}>
          <SubItem {...item} isFirst={index === 0} />
          {index < visibleItems.length - 1 && <Connector />}
        </div>
      ))}
      {canToggle && (
        <button
          type='button'
          onClick={toggle}
          aria-expanded={expanded}
          className={`flex items-start gap-2 pt-0.5 ${TIMELINE_DESIGN_TOKENS.typography.subItem} text-fg-tertiary hover:text-fg-secondary cursor-pointer bg-transparent border-none p-0`}
        >
          {/* Spacer matches the 18px sub-item icon column so the label aligns
              under the sub-item text rather than its icon. */}
          <span className='w-4.5 shrink-0' aria-hidden='true' />
          <span className='break-words text-left'>
            {expanded ? 'Show less' : moreLabel(hiddenCount)}
          </span>
        </button>
      )}
    </div>
  );
}

/**
 * Connector line between sub-items
 */
function Connector() {
  return (
    <div className='flex justify-start'>
      <div className='w-4.5 flex justify-center'>
        <div
          className={`${TIMELINE_DESIGN_TOKENS.spacing.verticalLine.subHeight} ${TIMELINE_DESIGN_TOKENS.spacing.verticalLine.main} ${TIMELINE_DESIGN_TOKENS.colors.timelineLine}`}
        />
      </div>
    </div>
  );
}

/**
 * Individual sub-item
 */
function SubItem({
  icon,
  text,
  href,
  linkText,
  ariaLabel,
  isFirst,
}: SubItemContent & { isFirst?: boolean }) {
  // First item gets extra padding to compensate for missing connector above it
  // All items get 2px top/bottom, but first item overrides top to 18px
  const padding = isFirst ? 'pt-4.5 pb-0.5' : 'py-0.5';
  return (
    <div
      className={`flex items-start gap-2 ${padding}`}
      role='listitem'
      aria-label={ariaLabel}
    >
      {/* Icon */}
      <div
        className={`${TIMELINE_DESIGN_TOKENS.spacing.iconSize.sub} flex items-center justify-center shrink-0`}
      >
        {icon}
      </div>

      {/* Text with optional link */}
      <div
        className={`${TIMELINE_DESIGN_TOKENS.typography.subItem} ${TIMELINE_DESIGN_TOKENS.colors.subItemText} flex-1 min-w-0`}
      >
        {href ? (
          <>
            {text}{' '}
            <a
              href={href}
              target='_blank'
              rel='noopener noreferrer'
              className='text-fg-brand-primary hover:underline font-medium truncate inline-block max-w-full align-bottom'
              title={linkText || href}
            >
              {linkText || href}
            </a>
          </>
        ) : (
          <span className='break-words'>{text}</span>
        )}
      </div>
    </div>
  );
}
